import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost } from './apiUtils';
import { Card, CardContent, CardHeader } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Calendar } from './components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './components/ui/popover';
import { Skeleton } from './components/ui/skeleton';
import { 
  Package, 
  TrendingUp, 
  IndianRupee, 
  BarChart3, 
  Upload, 
  FileText, 
  Wallet, 
  Download,
  CalendarIcon,
  ArrowUpRight,
  ArrowDownRight,
  Minus
} from 'lucide-react';
import { format } from 'date-fns';
import { useNavigation } from './contexts/NavigationContext';

// Helper function to get business date (day starts at 11:30 AM IST)
function getBusinessDate() {
  const now = new Date();
  
  // Check if browser is already in IST timezone
  const browserTimezoneOffset = now.getTimezoneOffset();
  const istTimezoneOffset = -330; // IST is UTC+5:30, so offset is -330 minutes
  
  let istTime;
  if (browserTimezoneOffset === istTimezoneOffset) {
    // Browser is already in IST (local machine), use current time
    istTime = now;
  } else {
    // Browser is in UTC or other timezone, convert to IST
    const istOffset = 5.5 * 60 * 60 * 1000; // 5.5 hours in milliseconds
    istTime = new Date(now.getTime() + istOffset);
  }
  
  if (istTime.getHours() < 11 || (istTime.getHours() === 11 && istTime.getMinutes() < 30)) {
    // Before 11:30 AM IST - use previous day
    const yesterday = new Date(istTime);
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toLocaleDateString('en-CA');
  } else {
    // After 11:30 AM IST - use current day
    return istTime.toLocaleDateString('en-CA');
  }
}

function Home() {
  const { navigate } = useNavigation();
  const [dashboardData, setDashboardData] = useState({
    stockValue: 0,
    stockLiftedInvoiceValue: 0,
    stockLiftedMrpValue: 0,
    todaysSale: 0,
    averageSales: 0,
    totalMonthlySales: 0,
    counterBalance: 0,
    totalAmountCollected: 0,
    balanceStatus: 'BALANCED'
  });
  const [loading, setLoading] = useState(true);
  const [fetchingData, setFetchingData] = useState(false);
  const [businessDate, setBusinessDate] = useState(getBusinessDate());
  const [shopSignupDate, setShopSignupDate] = useState(null);
  const [isManualDate, setIsManualDate] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Monitor business date changes (only if not manually set)
  useEffect(() => {
    if (isManualDate) {
      return;
    }

    const checkBusinessDate = () => {
      const newBusinessDate = getBusinessDate();
      if (newBusinessDate !== businessDate) {
        setBusinessDate(newBusinessDate);
      }
    };

    // Check immediately
    checkBusinessDate();
    
    // Check every minute for business date changes
    const interval = setInterval(checkBusinessDate, 60000);
    
    return () => clearInterval(interval);
  }, [businessDate, isManualDate]);

  const fetchDashboardData = useCallback(async () => {
    // Set appropriate loading state based on whether this is initial load or refetch
    if (loading) {
      // Initial load - don't change loading state here
    } else {
      // Refetching data - use fetchingData state
      setFetchingData(true);
    }
    
    try { 
      await apiPost('/api/stock/initialize-today');

      const response = await apiGet(`/api/summary?date=${businessDate}`);
      const data = await response.json();
      
      setDashboardData({
        stockValue: data.stockValue || 0,      
        stockLiftedInvoiceValue: data.stockLiftedInvoiceValue || 0,
        stockLiftedMrpValue: data.stockLiftedMrpValue || 0,
        todaysSale: data.totalSales || 0,
        averageSales: data.averageSales || 0,
        totalMonthlySales: data.totalMonthlySales || 0,
        counterBalance: data.counterBalance || 0,
        totalAmountCollected: data.totalAmountCollected || 0,
        balanceStatus: data.balanceStatus || 'BALANCED'
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardData({
        stockValue: 0,
        stockLiftedInvoiceValue: 0,
        stockLiftedMrpValue: 0,
        todaysSale: 0,
        averageSales: 0,
        totalMonthlySales: 0,
        counterBalance: 0,
        totalAmountCollected: 0,
        balanceStatus: 'BALANCED'
      });
    }
    setLoading(false);
    setFetchingData(false);
  }, [businessDate, loading]);

  // Fetch shop signup date on component mount
  useEffect(() => {
    const fetchShopSignupDate = async () => {
      try {
        const response = await apiGet('/api/shop/signup-date');
        const data = await response.json();
        setShopSignupDate(data.signupDate);
      } catch (error) {
        console.error('Error fetching shop signup date:', error);
        // Fallback to current date if API fails
        setShopSignupDate(new Date().toISOString().split('T')[0]);
      }
    };
    
    fetchShopSignupDate();
  }, []);


  // Fetch data when business date changes
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatBusinessDate = () => {
    // Format business date as DD-MM-YYYY
    const date = new Date(businessDate);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Get shop signup date for validation
  const getShopSignupDate = () => {
    return shopSignupDate || new Date().toISOString().split('T')[0];
  };

  // Validate selected date
  const validateDate = (dateString) => {
    const selected = new Date(dateString);
    const currentBusinessDate = new Date(getBusinessDate());
    const signupDate = new Date(getShopSignupDate());
    
    // Can't select future business dates
    if (selected > currentBusinessDate) {
      return false;
    }
    
    // Can't select dates before signup
    if (selected < signupDate) {
      return false;
    }
    
    return true;
  };


  if (loading) {
    return (
      <main className="w-full px-4 py-8 space-y-8 max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 w-full">
          <div className="space-y-2 text-left">
            <h1 className="text-4xl font-bold text-left">Dashboard</h1>
            <p className="text-base text-muted-foreground text-left">Welcome back! Here's your business overview.</p>
          </div>
          <Skeleton className="h-20 w-40 rounded-lg" />
        </div>

        {/* Metrics Grid Skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="overflow-hidden">
              <CardHeader className="pb-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-9 w-40" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                  <Skeleton className="h-12 w-12 rounded-full flex-shrink-0" />
                </div>
              </CardHeader>
            </Card>
          ))}
          {/* Counter Balance Skeleton - Full Width */}
          <Card className="overflow-hidden sm:col-span-2 lg:col-span-3">
            <CardHeader className="pb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <Skeleton className="h-16 w-16 rounded-full flex-shrink-0" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-10 w-48" />
                    <Skeleton className="h-3 w-64" />
                  </div>
                </div>
                <Skeleton className="h-6 w-24" />
              </div>
            </CardHeader>
          </Card>
        </div>

        {/* Quick Actions Section - Show immediately as they're static */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Quick Actions</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="cursor-pointer hover:shadow-xl hover:scale-[1.02] hover:border-primary transition-all group" onClick={() => navigate('uploadInvoice')}>
              <CardContent className="flex flex-col items-center justify-center p-8 space-y-4">
                <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all">
                  <Upload className="h-8 w-8 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
                <div className="text-center space-y-1">
                  <h3 className="font-bold text-lg">Upload Invoice</h3>
                  <p className="text-sm text-muted-foreground">Add new stock</p>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-xl hover:scale-[1.02] hover:border-primary transition-all group" onClick={() => navigate('updateClosingStock')}>
              <CardContent className="flex flex-col items-center justify-center p-8 space-y-4">
                <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all">
                  <FileText className="h-8 w-8 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
                <div className="text-center space-y-1">
                  <h3 className="font-bold text-lg">Closing Stock</h3>
                  <p className="text-sm text-muted-foreground">Update daily stock</p>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-xl hover:scale-[1.02] hover:border-primary transition-all group" onClick={() => navigate('incomeExpenses')}>
              <CardContent className="flex flex-col items-center justify-center p-8 space-y-4">
                <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all">
                  <Wallet className="h-8 w-8 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
                <div className="text-center space-y-1">
                  <h3 className="font-bold text-lg">Income/Expenses</h3>
                  <p className="text-sm text-muted-foreground">Track finances</p>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-xl hover:scale-[1.02] hover:border-primary transition-all group" onClick={() => navigate('downloadSaleSheet')}>
              <CardContent className="flex flex-col items-center justify-center p-8 space-y-4">
                <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all">
                  <Download className="h-8 w-8 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
                <div className="text-center space-y-1">
                  <h3 className="font-bold text-lg">Sale Sheet</h3>
                  <p className="text-sm text-muted-foreground">Download reports</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    );
  }

  const getBalanceStatusBadge = () => {
    const { balanceStatus } = dashboardData;
    if (balanceStatus === 'SHORT') {
      return (
        <Badge variant="destructive" className="flex items-center gap-1">
          <ArrowDownRight className="w-3 h-3" />
          Cash Short
        </Badge>
      );
    } else if (balanceStatus === 'SURPLUS') {
      return (
        <Badge variant="default" className="flex items-center gap-1">
          <ArrowUpRight className="w-3 h-3" />
          Cash Surplus
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="flex items-center gap-1">
        <Minus className="w-3 h-3" />
        Balanced
      </Badge>
    );
  };

  return (
    <main className="w-full px-4 py-8 space-y-8 max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 w-full">
          <div className="space-y-2 text-left">
            <h1 className="text-4xl font-bold text-left">Dashboard</h1>
            <p className="text-base text-muted-foreground text-left">Welcome back! Here's your business overview.</p>
          </div>
          
          <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
            <PopoverTrigger asChild>
              <div className="flex items-center gap-2 px-4 py-3 rounded-lg border bg-card cursor-pointer hover:bg-accent transition-colors">
                <CalendarIcon className="h-4 w-4" />
                <div className="text-left">
                  <div className="text-xs text-muted-foreground">Business Date</div>
                  <div className="text-base font-semibold">{formatBusinessDate()}</div>
                </div>
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={new Date(businessDate)}
                onSelect={(date) => {
                  if (date) {
                    const dateString = format(date, 'yyyy-MM-dd');
                    if (validateDate(dateString)) {
                      setIsManualDate(true);
                      setBusinessDate(dateString);
                      setIsCalendarOpen(false); // Close the popover after selection
                    }
                  }
                }}
                disabled={(date) => {
                  const dateString = format(date, 'yyyy-MM-dd');
                  return !validateDate(dateString);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Metrics Grid - Redesigned with better aesthetics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {fetchingData ? (
            // Show skeletons when refetching data
            <>
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <Card key={i} className={`overflow-hidden ${i === 7 ? 'sm:col-span-2 lg:col-span-3' : ''}`}>
                  <CardHeader className="pb-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-2 flex-1">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-9 w-40" />
                        <Skeleton className="h-3 w-full" />
                      </div>
                      <Skeleton className="h-12 w-12 rounded-full flex-shrink-0" />
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </>
          ) : (
            <>
              {/* Stock Value Card */}
              <Card className="overflow-hidden group hover:shadow-xl transition-all duration-300">
                <CardHeader className="pb-6 bg-gradient-to-br from-blue-50 to-transparent">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-muted-foreground mb-2 text-left">Stock Value</p>
                      <div className="text-3xl font-bold mb-2 text-left tabular-nums">{formatCurrency(dashboardData.stockValue)}</div>
                      <p className="text-xs text-muted-foreground text-left">Current inventory value</p>
                    </div>
                    <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                      <Package className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                </CardHeader>
              </Card>

          {/* Stock Lifted - Invoice */}
          <Card className="overflow-hidden group hover:shadow-xl transition-all duration-300">
            <CardHeader className="pb-6 bg-gradient-to-br from-green-50 to-transparent">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-muted-foreground mb-2 text-left">Stock Lifted (Invoice)</p>
                  <div className="text-3xl font-bold mb-2 text-left tabular-nums">{formatCurrency(dashboardData.stockLiftedInvoiceValue)}</div>
                  <p className="text-xs text-muted-foreground text-left">Total Stock Lifted at invoice price</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Stock Lifted - MRP */}
          <Card className="overflow-hidden group hover:shadow-xl transition-all duration-300">
            <CardHeader className="pb-6 bg-gradient-to-br from-emerald-50 to-transparent">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-muted-foreground mb-2 text-left">Stock Lifted (MRP)</p>
                  <div className="text-3xl font-bold mb-2 text-left tabular-nums">{formatCurrency(dashboardData.stockLiftedMrpValue)}</div>
                  <p className="text-xs text-muted-foreground text-left">Total Stock Lifted at MRP</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                  <TrendingUp className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Today's Sales */}
          <Card className="overflow-hidden group hover:shadow-xl transition-all duration-300">
            <CardHeader className="pb-6 bg-gradient-to-br from-purple-50 to-transparent">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-muted-foreground mb-2 text-left">Today's Sales</p>
                  <div className="text-3xl font-bold mb-2 text-left tabular-nums">{formatCurrency(dashboardData.todaysSale)}</div>
                  <p className="text-xs text-muted-foreground text-left">Revenue for current business day</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                  <BarChart3 className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Average Sales */}
          <Card className="overflow-hidden group hover:shadow-xl transition-all duration-300">
            <CardHeader className="pb-6 bg-gradient-to-br from-amber-50 to-transparent">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-muted-foreground mb-2 text-left">Average Sales</p>
                  <div className="text-3xl font-bold mb-2 text-left tabular-nums">{formatCurrency(dashboardData.averageSales)}</div>
                  <p className="text-xs text-muted-foreground text-left">Average daily sales</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                  <TrendingUp className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Monthly Sales */}
          <Card className="overflow-hidden group hover:shadow-xl transition-all duration-300">
            <CardHeader className="pb-6 bg-gradient-to-br from-orange-50 to-transparent">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-muted-foreground mb-2 text-left"> Total sales</p>
                  <div className="text-3xl font-bold mb-2 text-left tabular-nums">{formatCurrency(dashboardData.totalMonthlySales)}</div>
                  <p className="text-xs text-muted-foreground text-left">Total sales so far</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                  <BarChart3 className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Counter Balance */}
          <Card className="overflow-hidden group hover:shadow-xl transition-all duration-300 sm:col-span-2 lg:col-span-3">
            <CardHeader className="pb-6 bg-gradient-to-br from-indigo-50 to-transparent">
              <div className="flex items-center justify-between gap-6">
                <div className="flex items-center gap-6">
                  <div className="h-16 w-16 rounded-full bg-indigo-100 flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                    <IndianRupee className="h-8 w-8 text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-muted-foreground mb-2 text-left">Counter Balance</p>
                    <div className="text-4xl font-bold mb-2 text-left tabular-nums">{formatCurrency(dashboardData.counterBalance)}</div>
                    <p className="text-xs text-muted-foreground text-left">Current cash balance at counter</p>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {getBalanceStatusBadge()}
                </div>
              </div>
            </CardHeader>
          </Card>
            </>
          )}
        </div>

        {/* Quick Actions Section */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Quick Actions</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="cursor-pointer hover:shadow-xl hover:scale-[1.02] hover:border-primary transition-all group" onClick={() => navigate('uploadInvoice')}>
              <CardContent className="flex flex-col items-center justify-center p-8 space-y-4">
                <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all">
                  <Upload className="h-8 w-8 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
                <div className="text-center space-y-1">
                  <h3 className="font-bold text-lg">Upload Invoice</h3>
                  <p className="text-sm text-muted-foreground">Add new stock</p>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-xl hover:scale-[1.02] hover:border-primary transition-all group" onClick={() => navigate('updateClosingStock')}>
              <CardContent className="flex flex-col items-center justify-center p-8 space-y-4">
                <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all">
                  <FileText className="h-8 w-8 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
                <div className="text-center space-y-1">
                  <h3 className="font-bold text-lg">Closing Stock</h3>
                  <p className="text-sm text-muted-foreground">Update daily stock</p>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-xl hover:scale-[1.02] hover:border-primary transition-all group" onClick={() => navigate('incomeExpenses')}>
              <CardContent className="flex flex-col items-center justify-center p-8 space-y-4">
                <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all">
                  <Wallet className="h-8 w-8 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
                <div className="text-center space-y-1">
                  <h3 className="font-bold text-lg">Income/Expenses</h3>
                  <p className="text-sm text-muted-foreground">Track finances</p>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-xl hover:scale-[1.02] hover:border-primary transition-all group" onClick={() => navigate('downloadSaleSheet')}>
              <CardContent className="flex flex-col items-center justify-center p-8 space-y-4">
                <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all">
                  <Download className="h-8 w-8 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
                <div className="text-center space-y-1">
                  <h3 className="font-bold text-lg">Sale Sheet</h3>
                  <p className="text-sm text-muted-foreground">Download reports</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
  );
}

export default Home;
